import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const TREE = 'https://customer-assets.emergentagent.com/job_alchemy-village/artifacts/bc4plj5c_Alchemy%20Village%20Tree%20of%20Life.png';
const VILLAGE_MAP = 'https://customer-assets.emergentagent.com/job_988ca3c4-0353-4296-b3e4-3dfc4a85f5c2/artifacts/3zxe07fe_The%20Alchemy%20Village%206%20Paths%20to%20YOU..png';
const CABRINI = 'https://customer-assets.emergentagent.com/job_ea5fead4-27b7-4d15-9d4a-acaa6244f336/artifacts/2lazeg67_Cabrini%20with%20her%20arms%20out.jpg';
const WELCOME_IMG = 'https://customer-assets.emergentagent.com/job_b45e9d54-c6e0-4deb-b583-023a1a99ea3a/artifacts/pf5dpyze_Welcome%20to%20YOU...%20.png';
const HUMMINGBIRD = 'https://customer-assets.emergentagent.com/job_1b90f8c6-d70e-4858-8e6c-d4275accd79b/artifacts/l4bmy3e4_Official%20Hummingbird%20for%20TAV%20The%20Alchemy%20Village%20.png';
const THEME_SONG = 'https://customer-assets.emergentagent.com/job_b45e9d54-c6e0-4deb-b583-023a1a99ea3a/artifacts/q7j7cgm2_WELCOME%20TO%20The%20Alchemy%20Village.wav';

const SCENES = [
  { id: 0, duration: 3000, type: 'black' },
  { id: 1, duration: 5000, type: 'image', src: TREE, text: null, zoom: true },
  { id: 2, duration: 4000, type: 'text-only', lines: ['The', 'Alchemy', 'Village'] },
  { id: 3, duration: 4500, type: 'image', src: TREE, text: '"Pause long enough to hear\nthe God in YOU\ndirect your path."', zoom: false },
  { id: 4, duration: 5000, type: 'image', src: VILLAGE_MAP, text: '6 Paths to YOU', zoom: true },
  { id: 5, duration: 4500, type: 'image', src: CABRINI, text: 'Your guide.\nYour mirror.\nYour village.', zoom: false },
  { id: 6, duration: 5000, type: 'image', src: WELCOME_IMG, text: null, zoom: false },
  { id: 7, duration: 5000, type: 'final', text: 'Welcome to YOU.' },
];

export default function PromoPage() {
  const navigate = useNavigate();
  const [scene, setScene] = useState(-1);
  const [started, setStarted] = useState(false);
  const [fading, setFading] = useState(false);
  const audioRef = useRef(null);

  const startPromo = () => {
    setStarted(true);
    setScene(0);
    if (audioRef.current) {
      audioRef.current.volume = 0.8;
      audioRef.current.play().catch(() => {});
    }
  };

  useEffect(() => {
    if (scene < 0 || scene >= SCENES.length) return;
    const s = SCENES[scene];
    const timer = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        if (scene < SCENES.length - 1) {
          setScene(scene + 1);
          setFading(false);
        }
      }, 800);
    }, s.duration);
    return () => clearTimeout(timer);
  }, [scene]);

  const currentScene = scene >= 0 && scene < SCENES.length ? SCENES[scene] : null;

  return (
    <section className="promo" data-testid="promo-page">
      <audio ref={audioRef} src={THEME_SONG} preload="auto" />

      {/* Fireflies always present */}
      <div className="promo-particles">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="promo-particle" style={{
            left: `${10 + Math.random() * 80}%`,
            top: `${10 + Math.random() * 80}%`,
            animationDelay: `${Math.random() * 8}s`,
            animationDuration: `${6 + Math.random() * 6}s`,
          }} />
        ))}
      </div>

      {!started ? (
        <div className="promo-start" data-testid="promo-start">
          <img src={HUMMINGBIRD} alt="" className="promo-start-bird" />
          <h1 className="promo-start-title">The Alchemy Village</h1>
          <p className="promo-start-sub">A Cinematic Experience</p>
          <button className="promo-start-btn" data-testid="promo-play-btn" onClick={startPromo}>
            Play
          </button>
          <p className="promo-start-note">Turn your sound on</p>
        </div>
      ) : currentScene ? (
        <div className={`promo-scene ${fading ? 'promo-scene--fade' : ''}`} data-testid={`promo-scene-${scene}`}>

          {currentScene.type === 'black' && (
            <div className="promo-black">
              <p className="promo-black-text">Every transformation begins<br />with a single step...</p>
            </div>
          )}

          {currentScene.type === 'image' && (
            <div className="promo-img-scene">
              <img
                src={currentScene.src}
                alt=""
                className={`promo-scene-img ${currentScene.zoom ? 'promo-scene-img--zoom' : 'promo-scene-img--still'}`}
              />
              {currentScene.text && (
                <div className="promo-overlay-text">
                  {currentScene.text.split('\n').map((line, i) => (
                    <p key={i} className="promo-overlay-line" style={{ animationDelay: `${i * 0.5}s` }}>{line}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {currentScene.type === 'text-only' && (
            <div className="promo-text-scene">
              {currentScene.lines.map((line, i) => (
                <span key={i} className="promo-big-word" style={{ animationDelay: `${i * 0.6}s` }}>{line}</span>
              ))}
            </div>
          )}

          {currentScene.type === 'final' && (
            <div className="promo-final">
              <img src={HUMMINGBIRD} alt="" className="promo-final-bird" />
              <h2 className="promo-final-text">{currentScene.text}</h2>
              <button className="promo-final-btn" data-testid="promo-enter-btn" onClick={() => navigate('/')}>
                Enter The Village
              </button>
            </div>
          )}
        </div>
      ) : null}

      {started && (
        <button className="promo-skip" data-testid="promo-skip" onClick={() => navigate('/')}>
          Skip
        </button>
      )}
    </section>
  );
}
